import { trackById, trackByIndex } from 'src/app/shared/utils/track-by.util';
import { Component, Input, Output, EventEmitter, OnInit, OnChanges, OnDestroy, SimpleChanges, ViewChild, ViewContainerRef, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { FormsModule } from '@angular/forms';
import { DomSanitizer, SafeHtml, SafeResourceUrl } from '@angular/platform-browser';
import { MatDialog } from '@angular/material/dialog';
import { Subject, takeUntil, firstValueFrom, tap, switchMap, from, debounceTime, TimeoutError } from 'rxjs';
import { EditModeStateService } from '../../services/edit-mode-state.service';
import { ReportTemplateEditorComponent, EditorSavePayload, ReportEditorConfig, ReportEditorEvent, ReportEditorEventKind } from '../report-template-editor/report-template-editor.component';
import { EditorExitDialogComponent, EditorExitAction } from '../report-template-editor/editor-exit-dialog/editor-exit-dialog.component';
import { LivePreviewBuilderService } from '../report-template-editor/live-preview-builder.service';
import { EditorSaveDialogComponent, EditorSaveResult, SaveAction } from '../report-template-editor/editor-save-dialog/editor-save-dialog.component';
import { ReportTemplate, TemplateContext, TemplatePrintOptions, ReportTemplateJson, TemplateJson, UserInputField } from '../../models/reportTemplate.model';
import { ReportTemplateService } from '../../services/reportTemplate.service';
import { GenericTemplateService } from '../../services/generic-template.service';
import { OrganizationLevelService } from '../../services/organizationLevel.service';
import { ToastService } from 'src/app/services/toast.service';
import { EmailService } from 'src/app/services/email.service';
import { ReportService } from 'src/app/services/report.service';
import { FilterOption } from '../../services/template-mappers';
import { SharedModule } from 'src/app/shared/shared.module';
import { ENTITY_TYPES } from 'src/app/shared/widget-registry';
import { ReportWidgetsModule } from 'src/app/shared/report-widgets.module';
import { FileAttachment } from 'src/app/models/fileAttachment.model';
import { Lookup } from 'src/app/models/lookup.model';
import { CustomEntity } from 'src/app/models/customEntity.model';
import { EndpointsTableComponent } from '../endpoints-table/endpoints-table.component';
import { ISearchFieldData } from 'src/app/shared/grid/baseGrid.component';
import { SendEmailRequest } from 'src/app/models/email.model';
import { ConfirmDialogComponent } from 'src/app/shared/confirmDialog/confirmDialog.component';
import { ConfirmLeaveDialogComponent, ConfirmLeaveResult } from 'src/app/modals/confirm-leave-dialog/confirm-leave-dialog.component';
import { SampleService } from 'src/app/services/sample.service';
import { Sample } from 'src/app/models/sample.model';
import { EmailCompositionService, EmailFormState } from './services/email-form.service';
import { PdfGeneratorService } from './services/pdf-generator.service';
import { TemplateProcessorService } from './services/template-processor.service';
import { TranslateService } from 'src/app/services/translate.service';
import { AuthService } from 'src/app/services';
import { generateNextAvailableName } from 'src/app/shared/utils/view-name.util';
import { RichTextEditorComponent } from 'src/app/shared/rich-text-editor/rich-text-editor.component';
import { convertToEmailSafeHtml } from 'src/app/_helpers/email-html.helper';
import { createInertContainer, sanitizeReportHtml, stripExternalCssUrls } from 'src/app/shared/helpers/report-html-sanitizer.helper';
import { inlineImagesInLayoutHtml } from 'src/app/_helpers/image-inline.helper';
import { stripChipMarkupForStorage } from 'src/app/shared/rich-text-editor/placeholder-chip.plugin';
import { PdfGeneratedData } from 'src/app/models/pdfGeneratedData.model';
import { ColumnDetails } from 'src/app/models/columnDetails.model';
import { INJECTED_STYLES_ID } from '../report-template-editor/report-template-editor.constants';
import { ReportTemplateManagerBase } from 'src/app/shared/base/report-template-manager-base';
import { DateFormatService } from 'src/app/services/dateFormat.service';
import { buildReportFileName } from 'src/app/shared/utils/report-file-name.util';
import { ListFilterToolbarComponent } from 'src/app/shared/components/list-filter-toolbar/list-filter-toolbar.component';

export enum PreviewType {
  Html = 'html',
  Pdf = 'pdf'
}

@Component({
    standalone: true,
    selector: 'app-report-generation-popup',
    imports: [CommonModule, FormsModule, SharedModule, ReportWidgetsModule, RichTextEditorComponent,
        ReportTemplateEditorComponent, EditorExitDialogComponent, EditorSaveDialogComponent,
        ListFilterToolbarComponent],
    providers: [EmailCompositionService, PdfGeneratorService, TemplateProcessorService, LivePreviewBuilderService],
    templateUrl: './report-generation-popup.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./report-generation-popup.component.scss']
})
export class ReportGenerationPopupComponent extends ReportTemplateManagerBase implements OnInit, OnChanges, OnDestroy {
  @Input() entity!: any;
  @Input() entityType!: string;
  @Input() entityId!: string;
  @Input() entities?: Sample[];
  @Input() exemptedSamples?: Sample[];
  @Input() organizationLevelSummary?: Lookup;
  @Input() isVisible = false;
  @Input() organizationLevels?: Lookup[];
  @Input() isWaitingForData = false;
  @Input() columnDetails: Record<string, ColumnDetails> = {};
  @Output() close = new EventEmitter<void>();
  @Output() pdfGenerated = new EventEmitter<PdfGeneratedData>();
  @ViewChild('previewContainer', { static: false, read: ViewContainerRef }) previewContainer!: ViewContainerRef;

  private readonly editorContentChange$ = new Subject<string>();
  private recipientEntityMap = new Map<string, string>();
  private currentPdfBlobUrl?: string;
  private orgLevelsMap = new Map<number, string>();
  private cachedPdfBlob?: Blob;
  private cachedHtmlContent?: string;
  private pdfStateCacheKey?: string;

  lastCachedTemplateId?: string | number;
  lastCachedFilters?: string;
  lastGeneratedFileAttachment?: FileAttachment;
  selectedFormatType: PreviewType = PreviewType.Pdf;
  readonly PreviewType = PreviewType;
  showFormatSelector = false;
  isEmailMode = false;
  emailForm: EmailFormState = this.emailService.createEmptyForm();
  editingChip: { email: string; type: 'recipient' | 'cc' } | null = null;

  initialBoundEmployee: CustomEntity | null = null;
  selectedEmployeeId: string | null = null;
  selectedCcEmployeeId: string | null = null;
  employeeSearchTerm = '';
  ccEmployeeSearchTerm = '';

  printOptions: TemplatePrintOptions = {} as TemplatePrintOptions;
  selectedTemplate?: ReportTemplate;
  availableFilters: FilterOption[] = [];
  appliedFilterSelections: Record<string, any[]> = {};
  noFiltersAvailable = false;
  showSelectTemplateMessage = true;
  reportPreview: SafeHtml = '';
  pdfPreviewUrl: SafeResourceUrl | null = null;
  isPreviewLoading = false;
  previewType: PreviewType = PreviewType.Pdf;
  showLivePreview = true;
  templateUserInputs: Record<string, string | boolean> = {};
  pendingUserInputs: Record<string, string | boolean> = {};
  hasPendingChanges = false;
  templateContext?: TemplateContext;

  // Precomputed template bindings
  formattedExemptedSampleNumbers = '';
  exemptedSamplesSummary = '';
  isExemptedSamplesExpanded = false;
  employeeFullNameById = new Map<string, string>();
  changedFilterPaths = new Set<string>();
  changedInputKeys = new Set<string>();
  expandedFilterPaths = new Set<string>();
  filterSelectionSummaries = new Map<string, string>();
  filterSelectableCounts = new Map<string, number>();
  manuallyToggledFilterPaths = new Set<string>();
  appliedFiltersCount = 0;
  attachmentFilename = '';
  userInputKeys: string[] = [];
  lastSelectedFilterValues = new Map<string, unknown>();

  isEditMode = false;
  editHtmlSnapshot = '';
  editPreviewHtml: SafeHtml = '';
  liveEditorHtml: string | null = null;
  private liveEditorBaselineHtml: string | null = null;
  liveEditorPrintOptions: TemplatePrintOptions | null = null;
  leftPanelCollapsed = false;
  isPdfPreviewLoading = false;
  private editorSaveDialogOpen = false;

  get editorConfig(): ReportEditorConfig | null {
    if (!this.selectedTemplate || !this.templateContext) return null;
    return {
      templateJson: this.selectedTemplate.templateJson,
      entityType: this.entityType,
      templateContext: this.templateContext,
      htmlPreview: this.editPreviewHtml,
      showLivePreview: this.showLivePreview,
    };
  }

  private pdfBlobRevokeTimer: ReturnType<typeof setTimeout> | null = null;
  private pdfRenderFallbackTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly PDF_RENDER_FALLBACK_MS = 10000;
  private static readonly FILTER_AUTO_EXPAND_MAX_VALUES = 2;
  private templateStyleEl: HTMLStyleElement | null = null;
  private destroyed = false;

  constructor(
    templateService: ReportTemplateService,
    private genericTemplateService: GenericTemplateService,
    private sanitizer: DomSanitizer,
    dialog: MatDialog,
    private organizationLevelService: OrganizationLevelService,
    toastService: ToastService,
    private emailApiService: EmailService,
    private reportService: ReportService,
    public emailService: EmailCompositionService,
    private pdfGenerator: PdfGeneratorService,
    private templateProcessor: TemplateProcessorService,
    translateService: TranslateService,
    authService: AuthService,
    private sampleService: SampleService,
    public editModeState: EditModeStateService,
    private livePreviewBuilder: LivePreviewBuilderService,
    private dateFormatService: DateFormatService
  ) {
    super(templateService, toastService, translateService, authService, dialog);
  }

  async ngOnInit(): Promise<void> {
    const isMultiMode = !!this.entities?.length;
    if (isMultiMode) {
      this.entityType = ENTITY_TYPES.MULTI_SAMPLE;
      this.entity = this.entities;
    }

    const isSingleMode = !!this.entity && !!this.entityType && !!this.entityId;
    if (!isMultiMode && !isSingleMode) return;
    if (!this.genericTemplateService.canHandle(this.entityType)) return;

    this.emailService.setupSearch(
      term => this.loadEmployeesFor('recipient', term),
      term => this.loadEmployeesFor('cc', term)
    );

    this.editModeState.isEditWorkflowActive.pipe(takeUntil(this.destroy$))
      .subscribe(active => this.isEditMode = active);

    this.editorContentChange$.pipe(
      debounceTime(300),
      switchMap(html => from(this.buildLivePreviewWithWidgets(html))),
      takeUntil(this.destroy$)
    ).subscribe(styledHtml => {
      this.editPreviewHtml = this.sanitizer.bypassSecurityTrustHtml(styledHtml);
    });

    if (!this.isWaitingForData) {
      this.loadTemplates();
      this.analyzeFilters();
    }
    // Load custom fields once on init - they don't change during the component lifecycle
    await this.emailService.loadCustomFieldsAsync();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['exemptedSamples']) {
      this.formattedExemptedSampleNumbers = this.buildFormattedExemptedSampleNumbers();
      this.exemptedSamplesSummary = this.buildExemptedSamplesSummary();
      this.isExemptedSamplesExpanded = false;
    }

    const dataReadyChange = changes['isWaitingForData'];
    if (dataReadyChange && !dataReadyChange.firstChange &&
        dataReadyChange.previousValue && !this.isWaitingForData &&
        !this.availableTemplates.length) {
      this.loadTemplates();
      this.analyzeFilters();
      return;
    }

    if (changes['entity']?.currentValue) {
      this.analyzeFilters();
      if (this.selectedTemplate) this.updatePreview();
    }
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.destroy$.next();
    this.destroy$.complete();
    this.pdfGenerator.cleanupComponents();
    this.cleanupScrollListeners();
    this.removeTemplateStyles();
    if (this.currentPdfBlobUrl) {
      URL.revokeObjectURL(this.currentPdfBlobUrl);
    }
    if (this.pdfBlobRevokeTimer) {
      clearTimeout(this.pdfBlobRevokeTimer);
      this.pdfBlobRevokeTimer = null;
    }
    if (this.pdfRenderFallbackTimer) {
      clearTimeout(this.pdfRenderFallbackTimer);
      this.pdfRenderFallbackTimer = null;
    }
    this.editModeState.reset();
  }

  protected get templateEntityType(): string | undefined {
    return this.entityType;
  }

  /** True when this dialog is generating an Employee Letter (single-sample surface) rather than a Report. Drives letter-variant labels only — no behavioural change. */
  get isLetter(): boolean {
    return this.entityType === ENTITY_TYPES.EMPLOYEE_LETTER;
  }

  protected override onTemplatesLoaded(): void {
    if (this.filteredTemplates.length === 1 && !this.selectedTemplate) {
      this.selectTemplate(this.filteredTemplates[0]);
    }
  }

  protected override afterTemplateMutation(templateId: string): void {
    if (this.selectedTemplate?.id !== templateId) return;
    const refreshed = this.availableTemplates.find(t => t.id === templateId);
    if (refreshed) this.selectedTemplate = { ...refreshed };
  }

  protected override onTemplateDeleted(template: ReportTemplate): void {
    if (this.selectedTemplate?.id !== template.id) return;
    this.selectedTemplate = undefined;
    this.analyzeFilters();
  }

  private analyzeFilters(): void {
    if (!this.selectedTemplate) {
      this.availableFilters = [];
      this.appliedFilterSelections = {};
      this.noFiltersAvailable = false;
      this.showSelectTemplateMessage = true;
      this.appliedFiltersCount = 0;
      this.changedFilterPaths = new Set<string>();
      this.expandedFilterPaths = new Set<string>();
      this.manuallyToggledFilterPaths = new Set<string>();
      this.filterSelectionSummaries = new Map<string, string>();
      this.filterSelectableCounts = new Map<string, number>();
      this.lastSelectedFilterValues = new Map<string, unknown>();
      return;
    }

    this.availableFilters = this.genericTemplateService.getAvailableFilters(
      this.entity,
      this.entityType,
      this.selectedTemplate
    );
    this.noFiltersAvailable = this.availableFilters.length === 0;
    this.showSelectTemplateMessage = false;
    this.expandedFilterPaths = new Set<string>();
    this.manuallyToggledFilterPaths = new Set<string>();

    // Initialize applied filter selections to match current filter state
    this.appliedFilterSelections = this.availableFilters.reduce((acc, filter) => {
      acc[filter.path] = [...filter.selectedValues];
      return acc;
    }, {} as Record<string, any[]>);

    this.appliedFiltersCount = this.computeAppliedFiltersCount();
    this.changedFilterPaths = new Set<string>();
    this.rebuildLastSelectedFilterValues();
    this.rebuildFilterSelectionSummaries();
    this.applyFilterAutoExpansion();
  }

  selectTemplate(template: ReportTemplate): void {
    this.selectedTemplate = template;
    this.clearCache();
    this.initializeUserInputs();
    this.printOptions = (template.templateJson?.printOptions || {}) as TemplatePrintOptions;
    this.analyzeFilters();
    this.previewType = PreviewType.Pdf;
    this.updatePreview();
  }

  private clearCache(): void {
    this.cachedPdfBlob = undefined;
    this.cachedHtmlContent = undefined;
    this.lastCachedTemplateId = undefined;
    this.lastCachedFilters = undefined;
    this.pdfStateCacheKey = undefined;
    this.pdfGenerator.clearBlobCache();

    if (this.currentPdfBlobUrl) {
      URL.revokeObjectURL(this.currentPdfBlobUrl);
      this.currentPdfBlobUrl = undefined;
    }
  }

  private computePdfStateCacheKey(): string {
    if (!this.selectedTemplate) return '';
    return this.stableStringify({
      templateId: this.selectedTemplate.id,
      layoutHtml: this.normalizeHtmlForCache(this.selectedTemplate.templateJson.layoutHtml),
      printOptions: this.normalizePrintOptions(this.printOptions),
      userInputs: this.templateUserInputs,
      filters: this.getAppliedFilters(),
      entityId: this.entityId,
      entityType: this.entityType
    });
  }

  private toStorageHtml(html: string): string {
    return stripChipMarkupForStorage(html);
  }

  private normalizeHtmlForCache(html: string): string {
    return this.toStorageHtml(html).replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim();
  }

  private normalizePrintOptions(opts: TemplatePrintOptions | null | undefined): TemplatePrintOptions {
    if (!opts) return {} as TemplatePrintOptions;
    const m = opts.margins;
    const isLegacy = !m || m === '5mm' || m === '10mm';
    return { ...opts, margins: isLegacy ? '0mm' : m };
  }

  private stableStringify(value: unknown): string {
    if (value === null || value === undefined) return String(value);
    if (typeof value !== 'object') return JSON.stringify(value);
    if (Array.isArray(value)) return '[' + value.map(v => this.stableStringify(v)).join(',') + ']';
    const keys = Object.keys(value as Record<string, unknown>).sort();
    return '{' + keys.map(k => JSON.stringify(k) + ':' + this.stableStringify((value as Record<string, unknown>)[k])).join(',') + '}';
  }

  private initializeUserInputs(): void {
    if (!this.selectedTemplate) return;

    this.templateUserInputs = {};
    this.pendingUserInputs = {};
    this.hasPendingChanges = false;
    this.changedInputKeys = new Set<string>();

    const defaultInputs = this.genericTemplateService.getDefaultUserInputs(this.entityType);
    const declaredInputs = this.selectedTemplate.templateJson?.declaredFields?.userInputs;

    if (declaredInputs) {
      Object.entries(declaredInputs).forEach(([key, inputField]) => {
        let defaultValue: string | boolean = (inputField.default ?? defaultInputs[key] ?? '') as string | boolean;

        if (inputField.type === 'checkbox') {
          if (typeof defaultValue === 'string') {
            defaultValue = defaultValue.toLowerCase() === 'true';
          } else {
            defaultValue = Boolean(defaultValue);
          }
        }

        this.templateUserInputs[key] = defaultValue;
        this.pendingUserInputs[key] = defaultValue;
      });
      this.userInputKeys = Object.keys(declaredInputs);
    } else {
      this.userInputKeys = [];
    }
  }

  onFilterChange(): void {
    // Filter changes are now tracked as pending - don't apply immediately
    this.updatePendingChangesState();
  }

  onUserInputChange(): void {
    this.updatePendingChangesState();
  }

  private updatePendingChangesState(): void {
    const changedInputKeys = new Set<string>();
    let hasUserInputChanges = false;
    Object.keys(this.pendingUserInputs).forEach(key => {
      if (this.pendingUserInputs[key] !== this.templateUserInputs[key]) {
        changedInputKeys.add(key);
        hasUserInputChanges = true;
      }
    });
    this.changedInputKeys = changedInputKeys;

    const changedFilterPaths = new Set<string>();
    this.availableFilters.forEach(filter => {
      const appliedValues = this.appliedFilterSelections[filter.path] || [];
      const pendingValues = filter.selectedValues || [];
      const changed = appliedValues.length !== pendingValues.length ||
        !appliedValues.every(v => pendingValues.includes(v));
      if (changed) changedFilterPaths.add(filter.path);
    });
    this.changedFilterPaths = changedFilterPaths;

    this.hasPendingChanges = hasUserInputChanges || changedFilterPaths.size > 0;
  }

  private hasFilterChanges(): boolean {
    return this.availableFilters.some(filter => {
      const appliedValues = this.appliedFilterSelections[filter.path] || [];
      const pendingValues = filter.selectedValues || [];
      if (appliedValues.length !== pendingValues.length) return true;
      return !appliedValues.every(v => pendingValues.includes(v));
    });
  }

  hasFilterChanged(filterPath: string): boolean {
    const appliedValues = this.appliedFilterSelections[filterPath] || [];
    const filter = this.availableFilters.find(f => f.path === filterPath);
    const pendingValues = filter?.selectedValues || [];
    if (appliedValues.length !== pendingValues.length) return true;
    return !appliedValues.every(v => pendingValues.includes(v));
  }

  applyAllChanges(): void {
    // Apply user input changes
    this.templateUserInputs = { ...this.pendingUserInputs };

    // Apply filter changes
    this.appliedFilterSelections = this.availableFilters.reduce((acc, filter) => {
      acc[filter.path] = [...filter.selectedValues];
      return acc;
    }, {} as Record<string, any[]>);

    this.hasPendingChanges = false;
    this.changedFilterPaths = new Set<string>();
    this.changedInputKeys = new Set<string>();
    this.appliedFiltersCount = this.computeAppliedFiltersCount();
    this.clearCache();
    this.updatePreview();
  }

  discardAllChanges(): void {
    // Discard user input changes
    this.pendingUserInputs = { ...this.templateUserInputs };

    // Discard filter changes - restore applied selections to UI
    this.availableFilters.forEach(filter => {
      filter.selectedValues = [...(this.appliedFilterSelections[filter.path] || [])];
    });

    // Recompute cascading filter state (disabled flags) after restoring selections
    this.genericTemplateService.updateCascadingFilters(
      this.entity,
      this.entityType,
      this.availableFilters,
      'oelTypeId'  // Trigger from the root cascading filter to update dependent options
    );

    this.hasPendingChanges = false;
    this.changedFilterPaths = new Set<string>();
    this.changedInputKeys = new Set<string>();
    this.rebuildLastSelectedFilterValues();
    this.rebuildFilterSelectionSummaries();
    this.applyFilterAutoExpansion();
  }

  // Keep old methods as aliases for backwards compatibility
  applyUserInputChanges(): void {
    this.applyAllChanges();
  }

  discardUserInputChanges(): void {
    this.discardAllChanges();
  }

  private async updatePreview(): Promise<void> {
    if (!this.selectedTemplate) return;

    this.isPreviewLoading = true;

    try {
      const appliedFilters = this.getAppliedFilters();
      const hasAppliedFilters = Object.keys(appliedFilters).length > 0;
      const filteredData = hasAppliedFilters
        ? this.genericTemplateService.applyFilters(
            this.entity,
            this.entityType,
            appliedFilters
          )
        : undefined;

      this.templateContext = await this.genericTemplateService.mapToTemplateContext(
        this.entity,
        this.entityType,
        this.selectedTemplate,
        this.templateUserInputs,
        filteredData
      );

      if (this.templateContext) {
        this.templateContext.appliedFilters = this.availableFilters.map(f => ({
          path: f.path,
          selectedValues: f.selectedValues,
          availableValues: f.availableValues
        }));
      }

      if (this.templateContext?.userInputs) {
        Object.keys(this.templateUserInputs).forEach(key => {
          if (this.templateContext!.userInputs[key] !== undefined &&
            this.templateUserInputs[key] === this.pendingUserInputs[key]) {
            this.templateUserInputs[key] = this.templateContext!.userInputs[key];
            this.pendingUserInputs[key] = this.templateContext!.userInputs[key];
          }
        });
      }

      if (this.previewType === PreviewType.Html) {
        await this.generateHtmlPreview();
        this.isPreviewLoading = false;
      } else {
        await this.generatePdfPreview();
      }
    } catch (error) {
      this.reportPreview = this.sanitizer.bypassSecurityTrustHtml('<p>Error generating preview</p>');
      this.isPreviewLoading = false;
    }
  }

  private async generateHtmlPreview(): Promise<void> {
    if (!this.selectedTemplate || !this.templateContext) return;

    const tempContainer = document.createElement('div');
    document.body.appendChild(tempContainer);

    try {
      this.pdfGenerator.cleanupComponents();
      const processedHtml = this.templateProcessor.processPlaceholders(
        this.selectedTemplate.templateJson.layoutHtml,
        this.templateContext
      );
      tempContainer.innerHTML = sanitizeReportHtml(processedHtml);

      const transientRefs = await this.pdfGenerator.injectWidgets(
        tempContainer,
        this.entityType,
        this.templateContext,
        false
      );
      await this.pdfGenerator.waitForWidgets(transientRefs);
      const finalHtml = tempContainer.innerHTML;
      transientRefs.forEach(ref => ref.destroy());

      this.cachedHtmlContent = finalHtml;
      this.reportPreview = this.sanitizer.bypassSecurityTrustHtml(finalHtml);
    } catch (error) {
      this.reportPreview = this.sanitizer.bypassSecurityTrustHtml('<p>Error rendering template</p>');
    } finally {
      document.body.removeChild(tempContainer);
    }
  }

  private async generatePdfPreview(): Promise<void> {
    if (!this.selectedTemplate || !this.templateContext) return;

    try {
      if (this.currentPdfBlobUrl) URL.revokeObjectURL(this.currentPdfBlobUrl);

      const pdfBlob = await this.generatePdfBlob();
      this.cachedPdfBlob = pdfBlob;
      this.lastCachedTemplateId = this.selectedTemplate.id;
      this.lastCachedFilters = JSON.stringify(this.getAppliedFilters());

      const pdfUrl = URL.createObjectURL(pdfBlob);
      this.currentPdfBlobUrl = pdfUrl;
      this.pdfPreviewUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
        `${pdfUrl}#page=1&view=FitH&zoom=page-width&toolbar=1`
      );
      if (this.pdfRenderFallbackTimer) clearTimeout(this.pdfRenderFallbackTimer);
      this.pdfRenderFallbackTimer = setTimeout(() => {
        if (this.destroyed || this.currentPdfBlobUrl !== pdfUrl) return;
        this.isPreviewLoading = false;
      }, this.PDF_RENDER_FALLBACK_MS);
    } catch (error) {
      this.pdfPreviewUrl = null;
      this.isPreviewLoading = false;
      this.toastService.displayErrorMessage(this.getReportErrorMessage(error), true);
    }
  }

  onPdfPreviewRendered(event: Event): void {
    if (!this.isRealPdfFrame(event.target as HTMLIFrameElement | null)) return;
    this.isPreviewLoading = false;
    if (this.pdfRenderFallbackTimer) {
      clearTimeout(this.pdfRenderFallbackTimer);
      this.pdfRenderFallbackTimer = null;
    }
  }

  private isRealPdfFrame(frame: HTMLIFrameElement | null): boolean {
    if (!frame || !this.currentPdfBlobUrl) return false;
    try {
      const href = frame.contentWindow?.location.href ?? '';
      return href.split('#')[0] === this.currentPdfBlobUrl;
    } catch {
      // Reading location throws once the cross-origin PDF viewer owns the frame: assume the real document loaded.
      return true;
    }
  }

  async onGenerateReportForNewTab(openInNewTab = true, skipExistsCheck = false): Promise<void> {
    if (!this.selectedTemplate || !this.templateContext) return;

    if (openInNewTab && !skipExistsCheck) {
      const exists = await this.checkReportExists();
      if (exists && !(await this.confirmRegenerate())) return;
    }

    try {
      this.isLoading = true;

      let blob: Blob;
      let htmlContent = '';

      if (this.selectedFormatType === PreviewType.Pdf) {
        const result = await this.generatePdfBlobWithHtml();
        blob = result.blob;
        htmlContent = result.htmlContent;
      } else {
        htmlContent = await this.generateFinalHtmlContent();
        blob = new Blob([htmlContent], { type: 'text/html' });
      }

      const eventData: PdfGeneratedData = {
        blob,
        htmlContent,
        filename: this.generateFileName(this.selectedFormatType),
        entityId: this.entityId,
        entityType: this.entityType,
        template: this.selectedTemplate,
        templateJson: this.convertToTemplateJson(this.selectedTemplate.templateJson),
        userInputs: { ...this.templateUserInputs },
        filters: this.getAppliedFilters(),
        openInNewTab
      };

      // DASH-2751: Fail closed rather than hang. The save flow below awaits a promise that only a
      // host bound to (pdfGenerated) can settle (via onComplete/onError). If no host is listening,
      // emit() is a no-op, the promise never resolves, and the dialog spins forever with a clean
      // console (the exact RA-report bug). Assert an observer exists first so an unhandled output
      // surfaces a visible error and the `finally` still clears isLoading.
      if (!this.pdfGenerated.observed) {
        throw new Error('No (pdfGenerated) handler is bound; the report cannot be saved.');
      }

      await new Promise<void>((resolve, reject) => {
        this.pdfGenerated.emit({
          ...eventData,
          onComplete: (pdfFileAttachment?: FileAttachment) => {
            if (pdfFileAttachment) {
              this.lastGeneratedFileAttachment = pdfFileAttachment;
              this.attachmentFilename = this.computeAttachmentFilename();
            }
            resolve();
          },
          onError: reject
        });
      });
    } catch (error) {
      this.toastService.displayErrorMessage(this.getReportErrorMessage(error), true);
    } finally {
      this.isLoading = false;
    }
  }

  private getReportErrorMessage(error: unknown): string {
    if (error instanceof HttpErrorResponse && error.status === 429) {
      return this.translateService.getDynamicTranslation('Server is busy, please try again later');
    }
    // DASH-2756: server render timeout (504) or the client-side request timeout both mean the render
    // took too long — surface an actionable message instead of the generic failure so the user isn't
    // left staring at a spinner and knows to shrink the report.
    if ((error instanceof HttpErrorResponse && error.status === 504) || error instanceof TimeoutError) {
      return this.translateService.getDynamicTranslation('The report took too long to render. Try reducing its size or scale and generating again.');
    }
    return this.translateService.getDynamicTranslation(this.isLetter ? 'Failed to generate letter' : 'Failed to generate report');
  }

  private convertToTemplateJson(reportTemplateJson: TemplateJson): ReportTemplateJson {
    return {
      id: this.selectedTemplate!.id,
      name: this.selectedTemplate!.name,
      entityType: this.selectedTemplate!.entityType,
      status: reportTemplateJson.status ?? true,
      layoutHtml: reportTemplateJson.layoutHtml || '',
      assets: reportTemplateJson.assets || [],
      declaredFields: {
        bound: reportTemplateJson.declaredFields?.bound || [],
        userInputs: Object.entries(reportTemplateJson.declaredFields?.userInputs || {}).reduce((acc, [key, value]) => {
          const stored = this.templateUserInputs[key];
          const storedDefault = typeof stored === 'string' || typeof stored === 'boolean' ? stored : undefined;
          acc[key] = {
            label: value.label || key,
            type: value.type || 'text',
            default: storedDefault ?? value.default ?? ''
          };
          return acc;
        }, {} as Record<string, UserInputField>)
      },
      supportedFilters: (reportTemplateJson.supportedFilters || []).map(filter => ({
        path: filter.path || '',
        ui: { label: filter.ui?.label || '' }
      })),
      printOptions: reportTemplateJson.printOptions || { pageSize: 'A4', margins: '0mm' }
    };
  }

  async openSendEmailDialog(): Promise<void> {
    const exists = await this.checkReportExists();
    if (exists && !(await this.confirmRegenerate())) return;

    try {
      await this.onGenerateReportForNewTab(false);
    } catch (error) {
      this.toastService.displayErrorMessage(this.isLetter ? 'Failed to generate letter. Please try again' : 'Failed to generate report. Please try again', true);
      return;
    }

    this.isEmailMode = true;

    if (this.emailService.employees.length === 0) {
      await this.emailService.loadEmployeesAsync();
    }

    this.populateEmailTemplate();
  }

  private populateEmailTemplate(): void {
    const emailTemplate = this.genericTemplateService.getEmailTemplate(
      this.entity,
      this.entityType,
      this.emailService.customFields
    );

    if (!emailTemplate) return;

    this.emailForm.subject = emailTemplate.subject;
    this.emailForm.message = emailTemplate.message;
    this.emailForm.ccRecipients = emailTemplate.ccEmails || [];

    if (emailTemplate.recipientEmail && this.emailService.isValidEmail(emailTemplate.recipientEmail)) {
      this.emailForm.recipient = emailTemplate.recipientEmail;

      if (emailTemplate.recipientId) {
        this.recipientEntityMap.set(emailTemplate.recipientEmail, emailTemplate.recipientId);
        this.selectedEmployeeId = emailTemplate.recipientId;

        this.emailService.loadEmployeeByIdAsync(emailTemplate.recipientId).then(employee => {
          if (employee) {
            this.initialBoundEmployee = employee;
            const inList = this.emailService.filteredEmployees.some(e => e.id === emailTemplate.recipientId);
            if (!inList) {
              this.emailService.filteredEmployees = [employee, ...this.emailService.filteredEmployees];
            }
            this.rebuildEmployeeNameMap();
          }
        });
      }
    } else {
      this.emailForm.recipient = '';
      this.selectedEmployeeId = null;
      this.recipientEntityMap.clear();
    }
  }

  async getOrganizationLevelName(organizationLevelId: number): Promise<string> {
    if (this.orgLevelsMap.has(organizationLevelId)) {
      return this.orgLevelsMap.get(organizationLevelId) || '';
    }

    if (this.organizationLevels?.length) {
      const found = this.organizationLevels.find(org => org.id === organizationLevelId);
      if (found?.name) {
        this.orgLevelsMap.set(organizationLevelId, found.name);
        return found.name;
      }
    }

    try {
      const orgLevel = await firstValueFrom(
        this.organizationLevelService.getById(organizationLevelId)
      );

      if (orgLevel?.name) {
        this.orgLevelsMap.set(organizationLevelId, orgLevel.name);
        return orgLevel.name;
      }
    } catch (error) {
    }

    return '';
  }

  cancelEmailMode(): void {
    this.isEmailMode = false;
    this.emailForm = this.emailService.createEmptyForm();
    this.initialBoundEmployee = null;
  }

  removeEmailRecipient(): void {
    this.emailForm.recipient = '';
    this.recipientEntityMap.clear();
  }

  saveEmailRecipient(newEmail: string): void {
    const trimmed = newEmail.trim();
    if (!trimmed) {
      this.emailForm.recipient = '';
      return;
    }

    if (!this.emailService.isValidEmail(trimmed)) {
      this.toastService.displayErrorMessage('Invalid email format', true);
      return;
    }

    this.emailForm.recipient = trimmed;
  }

  addCcRecipient(): void {
    const email = this.emailForm.ccInput.trim();
    if (!email) return;

    if (!this.emailService.isValidEmail(email)) {
      this.toastService.displayErrorMessage('Invalid email format', true);
      return;
    }

    if (this.emailForm.ccRecipients.includes(email)) {
      this.toastService.displayErrorMessage('Email already added', true);
      return;
    }

    this.emailForm.ccRecipients.push(email);
    this.emailForm.ccInput = '';
  }

  removeCcRecipient(email: string): void {
    this.emailForm.ccRecipients = this.emailForm.ccRecipients.filter(e => e !== email);
  }

  editCcRecipient(email: string): void {
    this.editingChip = { email, type: 'cc' };
  }

  saveCcRecipient(oldEmail: string, newEmail: string): void {
    const trimmed = newEmail.trim();

    if (!trimmed || trimmed === oldEmail) {
      this.editingChip = null;
      return;
    }

    if (!this.emailService.isValidEmail(trimmed)) {
      this.toastService.displayErrorMessage('Invalid email format', true);
      this.editingChip = null;
      return;
    }

    if (this.emailForm.ccRecipients.includes(trimmed)) {
      this.toastService.displayErrorMessage('Email already exists', true);
      this.editingChip = null;
      return;
    }

    const index = this.emailForm.ccRecipients.indexOf(oldEmail);
    if (index !== -1) this.emailForm.ccRecipients[index] = trimmed;
    this.editingChip = null;
  }

  cancelEditRecipient(): void {
    this.editingChip = null;
  }

  async sendEmail(): Promise<void> {
    if (!this.emailForm.recipient?.trim()) {
      this.toastService.displayErrorMessage('Please add a recipient email', true);
      return;
    }
    if (!this.emailForm.subject?.trim()) {
      this.toastService.displayErrorMessage('Subject is required', true);
      return;
    }

    const recipientEntityId = this.recipientEntityMap.get(this.emailForm.recipient);
    if (!recipientEntityId) {
      this.toastService.displayErrorMessage('Could not find recipient employee ID', true);
      return;
    }

    if (!this.lastGeneratedFileAttachment?.id) {
      this.toastService.displayErrorMessage(this.isLetter ? 'Letter not generated. Please try again' : 'Report not generated. Please try again', true);
      return;
    }

    try {
      this.isLoading = true;

      const emailSafeBody = convertToEmailSafeHtml(this.emailForm.message);

      const payload: SendEmailRequest = {
        recipientEntityId,
        subject: this.emailForm.subject,
        body: emailSafeBody,
        ccRecipients: this.emailForm.ccRecipients.length > 0 ? this.emailForm.ccRecipients : undefined,
        fileAttachmentIds: [this.lastGeneratedFileAttachment.id]
      };

      this.emailApiService.sendEmail(payload).pipe(
        tap(() => {
          if (this.entityType === ENTITY_TYPES.SAMPLE || this.entityType === ENTITY_TYPES.EMPLOYEE_LETTER) {
            this.sampleService.markLetterSent([this.entityId])
              .pipe(takeUntil(this.destroy$))
              .subscribe({ error: (err) => this.toastService.displayErrorMessage('Failed to mark letter sent', true) });
          }
        }),
        takeUntil(this.destroy$)
      ).subscribe({
        next: () => this.onClose(),
        error: (error) => this.toastService.displayErrorMessage('Failed to send email', true)
      });
    } catch (error) {
      this.toastService.displayErrorMessage('Failed to send email', true);
    } finally {
      this.isLoading = false;
    }
  }

  async loadEmployeesFor(target: 'recipient' | 'cc', searchTerm = ''): Promise<void> {
    await this.emailService.loadEmployeesAsync(searchTerm, target);

    if (target === 'recipient' && this.initialBoundEmployee && !searchTerm.trim()) {
      const inList = this.emailService.filteredEmployees.some(e => e.id === this.initialBoundEmployee!.id);
      if (!inList) {
        this.emailService.filteredEmployees = [this.initialBoundEmployee, ...this.emailService.filteredEmployees];
      }
    }

    this.rebuildEmployeeNameMap();
  }

  onEmployeeSelected(employeeId: string): void {
    if (!employeeId) {
      this.selectedEmployeeId = null;
      return;
    }

    const selected = this.emailService.employees.find(e => e.id === employeeId);
    if (!selected) return;

    const email = this.emailService.getEmployeeEmail(selected);

    if (email && this.emailService.isValidEmail(email)) {
      this.selectedEmployeeId = employeeId;
      this.emailForm.recipient = email;
      this.recipientEntityMap.clear();
      this.recipientEntityMap.set(email, selected.id);
    } else {
      this.toastService.displayErrorMessage(email ? 'Invalid email address' : 'Employee email not found', true);
      this.selectedEmployeeId = null;
    }
  }

  onCcEmployeeSelected(employeeId: string): void {
    if (!employeeId) {
      this.selectedCcEmployeeId = null;
      return;
    }

    const selected = this.emailService.employees.find(e => e.id === employeeId);
    if (!selected) return;

    const email = this.emailService.getEmployeeEmail(selected);

    if (email && this.emailService.isValidEmail(email)) {
      if (!this.emailForm.ccRecipients.includes(email)) {
        this.emailForm.ccRecipients.push(email);
      }
      this.selectedCcEmployeeId = null;
    }
  }

  filterEmployeesLocally(searchTerm: string): void {
    this.employeeSearchTerm = searchTerm;
    this.emailService.triggerRecipientSearch(searchTerm);

    if (!searchTerm.trim() && this.initialBoundEmployee) {
      this.selectedEmployeeId = this.initialBoundEmployee.id;
    }
  }

  filterCcEmployeesLocally(searchTerm: string): void {
    this.ccEmployeeSearchTerm = searchTerm;
    this.emailService.triggerCcSearch(searchTerm);
  }

  onEmployeeDropdownScroll(event: any): void {
    const threshold = 100;
    const position = event.target.scrollTop + event.target.offsetHeight;
    const height = event.target.scrollHeight;

    if (position > height - threshold && !this.emailService.isLoading && this.emailService.hasMore) {
      this.emailService.loadMoreEmployeesAsync(this.employeeSearchTerm || this.ccEmployeeSearchTerm);
    }
  }

  async attachScrollListener(isOpen: boolean): Promise<void> {
    if (!isOpen) return;

    if (this.emailService.employees.length === 0) {
      await this.emailService.loadEmployeesAsync();
    }

    setTimeout(() => {
      const panels = document.querySelectorAll('.multiple-select-panel');
      const panel = panels.length > 0 ? panels[panels.length - 1] : null;

      if (panel) {
        panel.removeEventListener('scroll', this.handlePanelScroll);
        panel.addEventListener('scroll', this.handlePanelScroll);
      }
    }, 150);
  }

  private handlePanelScroll = (event: Event): void => {
    this.onEmployeeDropdownScroll(event);
  };

  private cleanupScrollListeners(): void {
    const panels = document.querySelectorAll('.multiple-select-panel');
    panels.forEach(panel => panel.removeEventListener('scroll', this.handlePanelScroll));
  }

  clearSelectedEmployee(): void {
    this.selectedEmployeeId = null;
    this.emailForm.recipient = '';
    this.recipientEntityMap.clear();
    this.initialBoundEmployee = null;
  }

  getSelectedEmployeeDisplay(): string {
    if (!this.selectedEmployeeId) return '';

    const employee = this.emailService.employees.find(e => e.id === this.selectedEmployeeId);

    if (!employee) {
      if (this.initialBoundEmployee && this.initialBoundEmployee.id === this.selectedEmployeeId) {
        const email = this.emailService.getEmployeeEmail(this.initialBoundEmployee);
        return email || this.emailService.getEmployeeFullName(this.initialBoundEmployee);
      }
      return '';
    }

    const email = this.emailService.getEmployeeEmail(employee);
    return email || this.emailService.getEmployeeFullName(employee);
  }

  getSelectedCcEmployeeDisplay(): string {
    if (!this.selectedCcEmployeeId) return '';
    const employee = this.emailService.employees.find(e => e.id === this.selectedCcEmployeeId);
    return employee ? this.emailService.getEmployeeFullName(employee) : '';
  }

  openEmployeeSearchDialog(isForCC = false): void {
    if (!this.emailService.coId) return;

    const data: ISearchFieldData = {
      coId: this.emailService.coId,
      selected: isForCC ? null : this.selectedEmployeeId,
      isMultiple: false
    };

    const dialogRef = this.dialog.open(EndpointsTableComponent, {
      width: '960px',
      data,
      panelClass: 'resizable-dialog'
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (!result) return;

      const { value, items } = result;
      if (value && items) {
        const email = this.emailService.getEmployeeEmail(items);

        if (this.emailService.isValidEmail(email)) {
          if (isForCC) {
            if (!this.emailForm.ccRecipients.includes(email)) {
              this.emailForm.ccRecipients.push(email);
            }
          } else {
            this.emailForm.recipient = email;
            this.selectedEmployeeId = items.id;
            this.recipientEntityMap.clear();
            this.recipientEntityMap.set(email, items.id);
          }
        }
      }
    });
  }

  getAttachmentFilename(): string {
    if (this.lastGeneratedFileAttachment?.name) {
      return this.lastGeneratedFileAttachment.name;
    }

    return this.generateFileName(this.selectedFormatType);
  }

  private computeAttachmentFilename(): string {
    return this.getAttachmentFilename();
  }

  private computeAppliedFiltersCount(): number {
    return Object.values(this.appliedFilterSelections).reduce((count, values) =>
      count + (values?.length || 0), 0
    );
  }

  private rebuildFilterSelectionSummaries(): void {
    const summaries = new Map<string, string>();
    const selectableCounts = new Map<string, number>();
    this.availableFilters.forEach(filter => {
      const total = filter.availableValues.filter(value => !value.disabled).length;
      const selected = filter.selectedValues.length;
      selectableCounts.set(filter.path, total);
      summaries.set(filter.path, total > 0 && selected === total
        ? this.translateService.getDynamicTranslation('all selected')
        : this.translateService.getDynamicTranslation('{{count}} selected', { count: selected }));
    });
    this.filterSelectionSummaries = summaries;
    this.filterSelectableCounts = selectableCounts;
  }

  private applyFilterAutoExpansion(): void {
    this.availableFilters.forEach(filter => {
      if (this.manuallyToggledFilterPaths.has(filter.path)) return;

      const selectable = this.filterSelectableCounts.get(filter.path) ?? 0;
      if (selectable > 0 && selectable <= ReportGenerationPopupComponent.FILTER_AUTO_EXPAND_MAX_VALUES) {
        this.expandedFilterPaths.add(filter.path);
      } else {
        this.expandedFilterPaths.delete(filter.path);
      }
    });
  }

  private rebuildLastSelectedFilterValues(): void {
    const map = new Map<string, unknown>();
    this.availableFilters.forEach(filter => {
      if (filter.selectedValues.length === 1) {
        map.set(filter.path, filter.selectedValues[0]);
      }
    });
    this.lastSelectedFilterValues = map;
  }

  private rebuildEmployeeNameMap(): void {
    const allEmployees = [
      ...this.emailService.filteredEmployees,
      ...this.emailService.filteredCcEmployees
    ];
    allEmployees.forEach(emp => {
      if (!this.employeeFullNameById.has(emp.id)) {
        this.employeeFullNameById.set(emp.id, this.emailService.getEmployeeFullName(emp));
      }
    });
  }

  private async generateFinalHtmlContent(): Promise<string> {
    if (!this.selectedTemplate || !this.templateContext) {
      throw new Error('No template or context available');
    }

    const tempContainer = document.createElement('div');
    document.body.appendChild(tempContainer);

    try {
      const processedHtml = this.templateProcessor.processPlaceholders(
        this.selectedTemplate.templateJson.layoutHtml,
        this.templateContext
      );
      tempContainer.innerHTML = sanitizeReportHtml(processedHtml);

      const transientRefs = await this.pdfGenerator.injectWidgets(
        tempContainer,
        this.entityType,
        this.templateContext,
        false
      );

      await this.pdfGenerator.waitForWidgets(transientRefs);

      const inlinedHtml = await inlineImagesInLayoutHtml(tempContainer.innerHTML);
      const finalHtml = this.pdfGenerator.buildStyledHtml(inlinedHtml, this.printOptions, this.entityType);

      transientRefs.forEach(ref => ref.destroy());
      return finalHtml;
    } finally {
      document.body.removeChild(tempContainer);
    }
  }

  async generatePdfBlob(): Promise<Blob> {
    const result = await this.generatePdfBlobWithHtml();
    return result.blob;
  }

  async generatePdfBlobWithHtml(): Promise<{ blob: Blob; htmlContent: string }> {
    if (!this.selectedTemplate || !this.templateContext) {
      throw new Error('No template or context available');
    }

    const stateKey = this.computePdfStateCacheKey();
    if (stateKey && stateKey === this.pdfStateCacheKey && this.cachedPdfBlob && this.cachedHtmlContent) {
      return { blob: this.cachedPdfBlob, htmlContent: this.cachedHtmlContent };
    }

    const tempContainer = document.createElement('div');
    document.body.appendChild(tempContainer);

    try {
      const processedHtml = this.templateProcessor.processPlaceholders(
        this.selectedTemplate.templateJson.layoutHtml,
        this.templateContext
      );
      tempContainer.innerHTML = sanitizeReportHtml(processedHtml);

      const transientRefs = await this.pdfGenerator.injectWidgets(
        tempContainer,
        this.entityType,
        this.templateContext,
        false
      );

      await this.pdfGenerator.waitForWidgets(transientRefs);

      const inlinedHtml = await inlineImagesInLayoutHtml(tempContainer.outerHTML);
      const result = await this.pdfGenerator.generatePdfBlob(inlinedHtml, this.printOptions, this.entityType);

      transientRefs.forEach(ref => ref.destroy());

      this.pdfStateCacheKey = stateKey;
      this.cachedPdfBlob = result.blob;
      this.cachedHtmlContent = result.htmlContent;
      return { blob: result.blob, htmlContent: result.htmlContent };
    } finally {
      document.body.removeChild(tempContainer);
    }
  }

  private generateFileName(extension: PreviewType = PreviewType.Pdf): string {
    const now = new Date();
    return buildReportFileName({
      entityType: this.entityType,
      subject: this.getFileNameSubject(),
      fallbackId: this.entityType === ENTITY_TYPES.MULTI_SAMPLE ? this.organizationLevelSummary?.id : this.entityId,
      sampleCount: this.entities?.length,
      datePart: this.dateFormatService.formatDateTimeWithTimeZoneToString(now, 'yyyy-MM-dd'),
      timePart: this.dateFormatService.formatDateTimeWithTimeZoneToString(now, 'HHmmss'),
      extension
    });
  }

  private getFileNameSubject(): string | undefined {
    switch (this.entityType) {
      case ENTITY_TYPES.SAMPLE_SURVEY:
        return this.entity?.survey?.name;
      case ENTITY_TYPES.SAMPLE:
      case ENTITY_TYPES.EMPLOYEE_LETTER:
        return this.entity?.sampleNumber;
      case ENTITY_TYPES.RISK_REGISTER:
        return this.entity?.riskAssessment?.name;
      case ENTITY_TYPES.MULTI_SAMPLE:
        return this.organizationLevelSummary?.name;
      default:
        return undefined;
    }
  }

  private async checkReportExists(): Promise<boolean> {
    if (this.entityType === ENTITY_TYPES.MULTI_SAMPLE) return false;
    try {
      return await firstValueFrom(this.reportService.existsForEntity(this.entityType, this.entityId));
    } catch {
      return false;
    }
  }

  private async confirmRegenerate(): Promise<boolean> {
    return new Promise((resolve) => {
      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        width: '400px',
        data: {
          title: this.translateService.getDynamicTranslation(this.isLetter ? 'Letter Already Exists' : 'Report Already Exists'),
          message: this.translateService.getDynamicTranslation(this.isLetter ? 'A letter for this entity already exists. Are you sure you want to generate a new one?' : 'A report for this entity already exists. Are you sure you want to generate a new one?'),
          primaryButtonText: this.translateService.getDynamicTranslation('Yes, generate new'),
          secondaryButtonText: this.translateService.getDynamicTranslation('Cancel'),
          primaryCallback: null,
          successMessage: '',
          errorMessage: ''
        }
      });

      dialogRef.afterClosed().subscribe(result => resolve(result === true));
    });
  }

  private getAppliedFilters(): Record<string, any[]> {
    // Use the committed/applied filter selections, not the pending UI state
    return { ...this.appliedFilterSelections };
  }

  onClose(): void {
    this.close.emit();
  }

  onOutsideClick(): void {
    if (this.isEditMode) {
      this.openEditorExitDialog();
      return;
    }

    if (!this.hasPendingChanges) {
      this.onClose();
      return;
    }

    const dialogRef = this.dialog.open(ConfirmLeaveDialogComponent, {
      width: '400px'
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result === ConfirmLeaveResult.Discard) {
        this.onClose();
      }
    });
  }

  toggleFilterValue(filter: FilterOption, value: any): void {
    const index = filter.selectedValues.indexOf(value);
    if (index > -1) {
      if (filter.selectedValues.length > 1) {
        filter.selectedValues.splice(index, 1);
      }
    } else {
      filter.selectedValues.push(value);
    }

    this.genericTemplateService.updateCascadingFilters(
      this.entity,
      this.entityType,
      this.availableFilters,
      filter.path
    );

    this.rebuildLastSelectedFilterValues();
    this.rebuildFilterSelectionSummaries();
  }

  getAppliedFiltersCount(): number {
    // Count from applied filter selections, not the pending UI state
    return Object.values(this.appliedFilterSelections).reduce((count, values) =>
      count + (values?.length || 0), 0
    );
  }

  isLastSelectedFilterOption(filter: FilterOption, value: any): boolean {
    return filter.selectedValues.length === 1 && filter.selectedValues.includes(value);
  }

  onFilterPanelToggle(filterPath: string, expanded: boolean): void {
    if (this.expandedFilterPaths.has(filterPath) === expanded) return;

    this.manuallyToggledFilterPaths.add(filterPath);
    if (expanded) {
      this.expandedFilterPaths.add(filterPath);
    } else {
      this.expandedFilterPaths.delete(filterPath);
    }
  }

  getInputPlaceholder(inputName: string): string {
    const placeholder = this.translateService.getDynamicTranslation('Enter {{inputName}}...', { inputName: inputName });
    return placeholder;
  }

  hasInputChanged(inputKey: string): boolean {
    return this.pendingUserInputs[inputKey] !== this.templateUserInputs[inputKey];
  }

  get filterState(): 'no-template' | 'no-filters' | 'filters-available' {
    if (this.showSelectTemplateMessage) return 'no-template';
    if (this.noFiltersAvailable) return 'no-filters';
    return 'filters-available';
  }

  get hasUserInputs(): boolean {
    return !!(this.selectedTemplate?.templateJson?.declaredFields?.userInputs &&
      Object.keys(this.selectedTemplate.templateJson.declaredFields.userInputs).length > 0);
  }

  get hasTemplates(): boolean {
    return this.filteredTemplates.length > 0;
  }

  get hasSelectedEmployee(): boolean {
    return this.selectedEmployeeId !== null;
  }

  get canSendEmail(): boolean {
    return !!this.emailForm.recipient?.trim() && !!this.emailForm.subject?.trim();
  }

  get emailValidationMessages(): string[] {
    const messages: string[] = [];
    if (!this.emailForm.recipient?.trim()) messages.push('Recipient\'s Email is required');
    if (!this.emailForm.subject?.trim()) messages.push('Subject is required');
    return messages;
  }

  get emailWarningMessages(): string[] {
    return !this.emailForm.message?.trim() ? ['Message is empty'] : [];
  }

  get canGenerateReport(): boolean {
    return !!this.selectedTemplate && !this.isLoading;
  }

  get emailRecipient(): string {
    return this.emailForm.recipient;
  }

  set emailRecipient(value: string) {
    this.emailForm.recipient = value;
  }

  get ccRecipients(): string[] {
    return this.emailForm.ccRecipients;
  }

  get emailSubject(): string {
    return this.emailForm.subject;
  }

  set emailSubject(value: string) {
    this.emailForm.subject = value;
  }

  get emailMessage(): string {
    return this.emailForm.message;
  }

  set emailMessage(value: string) {
    this.emailForm.message = value;
  }

  get ccRecipientInput(): string {
    return this.emailForm.ccInput;
  }

  set ccRecipientInput(value: string) {
    this.emailForm.ccInput = value;
  }

  get firstNameProperty(): string {
    return this.emailService.employeeFields.firstName;
  }

  get lastNameProperty(): string {
    return this.emailService.employeeFields.lastName;
  }

  get emailProperty(): string {
    return this.emailService.employeeFields.email;
  }

  get employeeTypeProperty(): string {
    return this.emailService.employeeFields.employeeType;
  }

  get positionProperty(): string {
    return this.emailService.employeeFields.position;
  }

  get employees(): CustomEntity[] {
    return this.emailService.employees;
  }

  get filteredEmployees(): CustomEntity[] {
    return this.emailService.filteredEmployees;
  }

  get filteredCcEmployees(): CustomEntity[] {
    return this.emailService.filteredCcEmployees;
  }

  get hasMoreEmployees(): boolean {
    return this.emailService.hasMore;
  }

  get isLoadingMoreEmployees(): boolean {
    return this.emailService.isLoading;
  }

  getEmployeeFullName(employee: CustomEntity): string {
    return this.emailService.getEmployeeFullName(employee);
  }

  private buildFormattedExemptedSampleNumbers(): string {
    const numbers = (this.exemptedSamples ?? []).map(s => s.sampleNumber);
    if (numbers.length === 0) return '';
    if (numbers.length === 1) return numbers[0];
    const and = this.translateService.getDynamicTranslation('and');
    return `${numbers.slice(0, -1).join(', ')} ${and} ${numbers[numbers.length - 1]}`;
  }

  private buildExemptedSamplesSummary(): string {
    const count = this.exemptedSamples?.length ?? 0;
    return count === 1
      ? this.translateService.getDynamicTranslation('1 sample excluded')
      : this.translateService.getDynamicTranslation('{{count}} samples excluded', { count });
  }

  toggleExemptedSamplesExpanded(): void {
    this.isExemptedSamplesExpanded = !this.isExemptedSamplesExpanded;
  }

  public trackById = trackById;
  public trackByIndex = trackByIndex;

  private injectTemplateStyles(layoutHtml: string): void {
    this.removeTemplateStyles();
    // Read the blocks the HTML parser and sanitizer produce, not a regex match on the raw template,
    // so a crafted </style> inside a CSS string cannot change what reaches the page.
    const parsed = createInertContainer();
    parsed.innerHTML = sanitizeReportHtml(layoutHtml);
    const styleBlocks = Array.from(parsed.querySelectorAll('style'), style => style.textContent ?? '');
    if (styleBlocks.length === 0) return;
    const el = document.createElement('style');
    el.id = INJECTED_STYLES_ID;
    el.textContent = stripExternalCssUrls(styleBlocks.join('\n'));
    document.head.appendChild(el);
    this.templateStyleEl = el;
  }

  private removeTemplateStyles(): void {
    if (this.templateStyleEl) {
      this.templateStyleEl.remove();
      this.templateStyleEl = null;
    }
    const existing = document.getElementById(INJECTED_STYLES_ID);
    if (existing) existing.remove();
  }

  async requestEditMode(): Promise<void> {
    if (!this.selectedTemplate) return;

    const confirmed = await this.confirmEditEntry();
    if (!confirmed) return;

    this.editHtmlSnapshot = this.selectedTemplate.templateJson.layoutHtml;
    this.injectTemplateStyles(this.editHtmlSnapshot);
    this.liveEditorHtml = null;
    this.liveEditorBaselineHtml = null;
    const savedOpts = this.selectedTemplate.templateJson.printOptions;
    this.liveEditorPrintOptions = savedOpts ? ({ ...savedOpts } as TemplatePrintOptions) : null;
    this.showLivePreview = true;
    this.leftPanelCollapsed = true;
    this.editModeState.confirmEntry();

    await this.generateHtmlPreview();
    const styledHtml = await this.buildLivePreviewWithWidgets(this.editHtmlSnapshot);
    if (this.destroyed) return;
    this.editPreviewHtml = this.sanitizer.bypassSecurityTrustHtml(styledHtml);
  }

  private async confirmEditEntry(): Promise<boolean> {
    this.editModeState.requestEditMode();
    return new Promise(resolve => {
      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        width: '420px',
        panelClass: 'edit-entry-dialog',
        data: {
          title: this.translateService.getDynamicTranslation('Enter Edit Mode'),
          message: this.translateService.getDynamicTranslation('Entering edit mode locks filters. You won\'t be able to change them while editing. Continue?'),
          primaryButtonText: this.translateService.getDynamicTranslation('Enter Edit Mode'),
          secondaryButtonText: this.translateService.getDynamicTranslation('Cancel'),
          primaryCallback: null,
          successMessage: '',
          errorMessage: ''
        }
      });
      dialogRef.afterClosed().subscribe(result => {
        if (!result) {
          this.editModeState.cancelEntry();
        }
        resolve(result === true);
      });
    });
  }

  onEditorEvent(e: ReportEditorEvent): void {
    switch (e.kind) {
      case ReportEditorEventKind.Saved:
        this.onEditorSaved(e.payload);
        break;
      case ReportEditorEventKind.Discarded:
        this.onEditorDiscarded();
        break;
      case ReportEditorEventKind.GeneratePdf:
        this.onEditorGeneratePdf();
        break;
      case ReportEditorEventKind.TogglePreview:
        this.toggleLivePreview();
        break;
      case ReportEditorEventKind.PrintOptionsChange:
        this.onLiveEditorPrintOptionsChanged(e.printOptions);
        break;
      case ReportEditorEventKind.ContentChange:
        this.liveEditorHtml = e.html;
        if (this.liveEditorBaselineHtml === null) this.liveEditorBaselineHtml = e.html;
        this.editorContentChange$.next(e.html);
        break;
    }
  }

  async onEditorSaved(payload: EditorSavePayload): Promise<void> {
    if (!this.selectedTemplate || !this.templateContext) return;
    if (this.editorSaveDialogOpen) return;

    const savedLayoutHtml = payload.layoutHtml;
    const savedPrintOptions = payload.printOptions as TemplatePrintOptions;
    const template = this.selectedTemplate;
    const availableNames = this.availableTemplates.map(t => t.name);
    const firstName = this.authService.getCurrentUser()?.userFullName?.split(' ')[0] || 'User';
    const defaultNewName = generateNextAvailableName(`${firstName}'s ${template.name}`, availableNames, { includeBare: true });

    this.editorSaveDialogOpen = true;
    return new Promise<void>(resolve => {
      const dialogRef = this.dialog.open(EditorSaveDialogComponent, {
        width: '600px',
        minWidth: '520px',
        panelClass: 'editor-save-dialog',
        data: {
          defaultNewName,
          isDefault: template.isDefault,
          canManage: template.canManage,
          availableTemplates: availableNames
        }
      });

      dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe(async (result: EditorSaveResult | null | undefined) => {
        this.editorSaveDialogOpen = false;
        if (!result) {
          resolve();
          return;
        }

        this.isLoading = true;
        this.removeTemplateStyles();
        this.editModeState.beginPersist();

        try {
          if (result.save === SaveAction.Override) {
            const updatePayload = this.templateService.buildUpdatePayload(
              { ...template, templateJson: { ...template.templateJson, layoutHtml: savedLayoutHtml, printOptions: savedPrintOptions } },
              savedLayoutHtml,
              savedPrintOptions
            );
            await firstValueFrom(this.templateService.update(updatePayload));
          } else if (result.save === SaveAction.SaveAsNew) {
            if (!result.name) {
              throw new Error('A name is required to save a new template.');
            }
            const createPayload = this.templateService.buildCreatePayload(
              template,
              result.name,
              savedLayoutHtml,
              savedPrintOptions,
              result.share ?? false
            );
            const created = await firstValueFrom(this.templateService.create(createPayload));
            // Override keeps the same id, so the existing selectedTemplate stays valid for the
            // post-reload re-selection below; SaveAsNew has a new id, so point at the created row.
            this.selectedTemplate = this.templateService.convertToReportTemplate(created);
          }

          this.editModeState.persistSuccess();
          this.liveEditorHtml = null;
          this.liveEditorPrintOptions = null;
          this.leftPanelCollapsed = false;
          this.toastService.showSuccess(this.translateService.getDynamicTranslation('Template saved successfully'));
        } catch (error) {
          this.toastService.showError(error, this.translateService.getDynamicTranslation('Save failed. Please try again.'));
          this.editModeState.persistFailure();
          this.isLoading = false;
          resolve();
          return;
        }

        try {
          await this.loadTemplates();
          const savedId = this.selectedTemplate?.id;
          const refreshed = this.availableTemplates.find(t => t.id === savedId);
          if (refreshed && this.selectedTemplate !== refreshed) this.selectTemplate(refreshed);
        } catch (error) {
          this.toastService.showError(error, this.translateService.getDynamicTranslation('Failed to refresh templates'));
        } finally {
          this.isLoading = false;
        }

        resolve();
      });
    });
  }

  onEditorDiscarded(): void {
    this.editModeState.requestDiscard();
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '400px',
      panelClass: 'discard-changes-dialog',
      data: {
        title: this.translateService.getDynamicTranslation('Discard Changes'),
        message: this.translateService.getDynamicTranslation('All edits will be lost. Continue?'),
        primaryButtonText: this.translateService.getDynamicTranslation('Discard'),
        secondaryButtonText: this.translateService.getDynamicTranslation('Keep Editing'),
        primaryCallback: null,
        successMessage: '',
        errorMessage: ''
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result === true) {
        if (this.selectedTemplate) {
          this.selectedTemplate = {
            ...this.selectedTemplate,
            templateJson: {
              ...this.selectedTemplate.templateJson,
              layoutHtml: this.editHtmlSnapshot
            }
          };
        }
        this.liveEditorHtml = null;
        this.leftPanelCollapsed = false;
        this.removeTemplateStyles();
        this.editModeState.beginRevert();
        this.editModeState.revertComplete();
      } else {
        this.editModeState.confirmEntry();
      }
    });
  }

  async onEditorGeneratePdf(): Promise<void> {
    if (!this.selectedTemplate || !this.templateContext) return;

    const rawHtml = this.liveEditorHtml ?? this.selectedTemplate.templateJson.layoutHtml;
    const layoutHtml = this.toStorageHtml(rawHtml);
    const printOptions = (this.liveEditorPrintOptions ?? this.selectedTemplate.templateJson.printOptions ?? {}) as TemplatePrintOptions;

    const editorMatchesSaved =
      this.normalizeHtmlForCache(layoutHtml) === this.normalizeHtmlForCache(this.selectedTemplate.templateJson.layoutHtml) &&
      this.stableStringify(this.normalizePrintOptions(printOptions)) ===
        this.stableStringify(this.normalizePrintOptions(this.selectedTemplate.templateJson.printOptions));

    if (editorMatchesSaved) {
      try {
        this.isPdfPreviewLoading = true;
        const result = await this.generatePdfBlobWithHtml();
        const pdfUrl = URL.createObjectURL(result.blob);
        window.open(pdfUrl, '_blank');
        if (this.pdfBlobRevokeTimer) clearTimeout(this.pdfBlobRevokeTimer);
        this.pdfBlobRevokeTimer = setTimeout(() => URL.revokeObjectURL(pdfUrl), 30000);
      } catch {
        this.toastService.displayErrorMessage('Failed to generate PDF preview', true);
      } finally {
        this.isPdfPreviewLoading = false;
      }
      return;
    }

    const tempContainer = document.createElement('div');
    document.body.appendChild(tempContainer);

    try {
      this.isPdfPreviewLoading = true;
      const processedHtml = this.templateProcessor.processPlaceholders(layoutHtml, this.templateContext);
      tempContainer.innerHTML = sanitizeReportHtml(processedHtml);

      const transientRefs = await this.pdfGenerator.injectWidgets(
        tempContainer, this.entityType, this.templateContext, false
      );
      await this.pdfGenerator.waitForWidgets(transientRefs);

      const inlinedHtml = await inlineImagesInLayoutHtml(tempContainer.outerHTML);
      const result = await this.pdfGenerator.generatePdfBlob(inlinedHtml, printOptions, this.entityType);
      transientRefs.forEach(ref => ref.destroy());

      const blob = result.blob;
      const pdfUrl = URL.createObjectURL(blob);
      window.open(pdfUrl, '_blank');
      if (this.pdfBlobRevokeTimer) clearTimeout(this.pdfBlobRevokeTimer);
      this.pdfBlobRevokeTimer = setTimeout(() => URL.revokeObjectURL(pdfUrl), 30000);
    } catch {
      this.toastService.displayErrorMessage('Failed to generate PDF preview', true);
    } finally {
      this.isPdfPreviewLoading = false;
      document.body.removeChild(tempContainer);
    }
  }

  private openEditorExitDialog(): void {
    this.editModeState.requestExit();
    const dialogRef = this.dialog.open(EditorExitDialogComponent, { width: '480px' });

    dialogRef.afterClosed().subscribe((action: EditorExitAction | undefined) => {
      if (!action || action === EditorExitAction.Stay) {
        this.editModeState.stayEditing();
        return;
      }
      if (action === EditorExitAction.Discard) {
        if (this.selectedTemplate) {
          this.selectedTemplate = {
            ...this.selectedTemplate,
            templateJson: {
              ...this.selectedTemplate.templateJson,
              layoutHtml: this.editHtmlSnapshot
            }
          };
        }
        this.liveEditorHtml = null;
        this.leftPanelCollapsed = false;
        this.removeTemplateStyles();
        this.editModeState.beginRevert();
        this.editModeState.revertComplete();
      }
      if (action === EditorExitAction.Save) {
        const rawHtml = this.liveEditorHtml ?? this.selectedTemplate!.templateJson.layoutHtml;
        const layoutHtml = this.toStorageHtml(rawHtml);
        const printOptions = (this.liveEditorPrintOptions ?? this.selectedTemplate!.templateJson.printOptions ?? {}) as TemplatePrintOptions;
        this.onEditorSaved({ layoutHtml, printOptions }).then(() => this.onClose());
      }
    });
  }

  get canEditTemplate(): boolean {
    return !!this.selectedTemplate && !this.isEditMode && !this.isLoading && !this.isEmailMode;
  }

  toggleLivePreview(): void {
    this.showLivePreview = !this.showLivePreview;
  }

  toggleLeftPanel(): void {
    this.leftPanelCollapsed = !this.leftPanelCollapsed;
  }

  onLiveEditorPrintOptionsChanged(opts: TemplatePrintOptions): void {
    this.liveEditorPrintOptions = opts;
    if (this.liveEditorHtml) {
      this.editorContentChange$.next(this.liveEditorHtml);
    }
  }

  private async buildLivePreviewWithWidgets(html: string): Promise<string> {
    if (!this.isEditMode || !this.selectedTemplate || !this.templateContext) return sanitizeReportHtml(html);
    return this.livePreviewBuilder.buildHtml({
      html,
      entityType: this.entityType,
      templateContext: this.templateContext,
      printOptions: this.liveEditorPrintOptions ?? this.selectedTemplate.templateJson.printOptions
    });
  }

}
